import { useQuery } from "@apollo/client";
import useCapabilities, { useCanViewCommitteesBool, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { useFlags } from "@/hooks/useFeatureFlags";
import { getSectionOperationsVisibility } from "../_utils";
import { SECTION_NAV_QUERY, SectionNavQueryResponse } from "../queries";
import { useSectionCommitteeManagementGate } from "./useSectionCommitteeManagementEnabled";

export function useSectionOperationsAccess(sectionId?: string) {
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const flags = useFlags();
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const canViewCommittees = useCanViewCommitteesBool();
  const { enabled: sectionCommitteeManagementEnabled, flagsLoaded } = useSectionCommitteeManagementGate();
  const governanceEnabled = flags["backoffice-section-governance"] === true;
  const skipSectionOfficeQuery = !sectionId || !canViewFacilities;
  const { data, loading, error } = useQuery<SectionNavQueryResponse>(SECTION_NAV_QUERY, {
    variables: { sectionId: sectionId ?? "" },
    skip: skipSectionOfficeQuery,
    fetchPolicy: "cache-first"
  });

  const sectionOfficeFacilityId = data?.section?.sectionOffice?.id ?? null;
  const sectionNavError = skipSectionOfficeQuery ? null : (error ?? null);
  const visibility = getSectionOperationsVisibility({
    sectionOfficeFacilityId,
    canViewFacilities,
    canViewCommittees,
    sectionCommitteeManagementEnabled
  });
  // Keep facility Operations tabs reachable when SECTION_NAV fails so pages can show an error
  // instead of looking like "no office" / ejecting the user to Jobs.
  const showSectionOfficeFacilityTabs =
    visibility.showSectionOfficeFacilityTabs || Boolean(canViewFacilities && sectionNavError);

  return {
    ready: capabilitiesLoaded && flagsLoaded && (skipSectionOfficeQuery || !loading || data != null || error != null),
    sectionOfficeFacilityId,
    sectionNavError,
    canViewFacilities,
    showCommittees: visibility.showCommittees,
    showSectionOfficeFacilityTabs,
    governanceEnabled,
    showGovernance: showSectionOfficeFacilityTabs && governanceEnabled,
    showOperations: visibility.showCommittees || showSectionOfficeFacilityTabs
  };
}
